import { db } from './firebase.js';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';

const INQUIRIES_COLLECTION = 'inquiries';
const CLIENTS_COLLECTION = 'clients';

/**
 * Returns the central lead intake API URL on verveailabs.com
 */
export function getLeadsApiUrl() {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LEADS_API_URL) {
    return import.meta.env.VITE_LEADS_API_URL;
  }
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3000/api/contact';
    }
  }
  return 'https://verveailabs.com/api/contact';
}

/**
 * Dispatches lead to central API to save to common Firestore and trigger immediate alerts
 * to Company Number (+91 9415967302) and contact@verveailabs.com
 */
export async function dispatchLeadToCentralApi(payload) {
  try {
    const url = getLeadsApiUrl();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn(`[CENTRAL LEADS API] Server responded with status ${res.status}`);
      return { success: false, status: res.status };
    }
    const data = await res.json();
    console.info('[CENTRAL LEADS API] Successfully dispatched lead & triggered immediate alerts:', data);
    return { success: true, inquiryId: data.inquiryId, alerts: data.alerts };
  } catch (err) {
    console.warn('[CENTRAL LEADS API] Network dispatch notice (relying on direct Firestore):', err);
    return { success: false, error: err?.message };
  }
}

/**
 * Generate a unique, recognizable lead identifier
 */
export function generateLeadId(startupName) {
  const cleanName = (startupName || 'lead')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10);
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `lead_${cleanName}_${Date.now().toString(36)}_${randomSuffix}`;
}

/**
 * STEP 1: The 10-Second Hook (Captures Lead in Common Firestore & Dispatches Immediate Alerts)
 * Stage: STEP_1_CAPTURED | Score: 75/100 | Priority: HIGH PRIORITY
 */
export async function saveStep1Lead({
  startupName,
  whatsapp,
  email,
  founderName = '',
  existingLeadId = null,
}) {
  const leadId = existingLeadId || generateLeadId(startupName);
  const cleanedEmail = (email || '').trim().toLowerCase();
  const cleanedPhone = (whatsapp || '').trim();
  const displayName = founderName.trim() || startupName.trim();
  const effectiveMessage = `Progressive Launchpad Lead for ${startupName}. Verified Mobile: ${cleanedPhone}`;

  const payload = {
    id: leadId,
    leadId,
    inquiryId: leadId,
    name: displayName,
    founderName: founderName.trim() || null,
    startupName: startupName.trim(),
    company: startupName.trim(),
    whatsapp: cleanedPhone,
    phone: cleanedPhone,
    phoneOrWhatsapp: cleanedPhone,
    phoneVerified: true,
    email: cleanedEmail,
    status: 'new',
    stage: 'STEP_1_CAPTURED',
    priority: 'high',
    score: 75,
    leadScore: 75,
    promoCode: 'LAUNCH50',
    discountClaimed: '50% OFF (₹4,999)',
    packageType: 'Turnkey Startup Launchpad',
    finalPrice: 4999,
    currency: 'INR',
    serviceTags: ['Website', 'AI Agent', 'Custom Domain Email', 'Domain'],
    capabilities: ['Turnkey Startup Launchpad', 'Website', 'AI Agent', 'Custom Domain Email', 'Domain'],
    tags: ['Launchpad', 'LAUNCH50', 'Verified Mobile', 'verveai.co'],
    source: 'https://verveai.co',
    message: effectiveMessage,
    resumeUrl: `https://verveai.co/resume/${leadId}`,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAtIso: new Date().toISOString(),
  };

  // 1. Dispatch to Central API for instant alerts to +91 9415967302 & contact@verveailabs.com
  dispatchLeadToCentralApi(payload).catch((apiErr) => {
    console.warn('Central API dispatch warning:', apiErr);
  });

  try {
    // 2. Also write to common `inquiries` Firestore collection directly
    await setDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload, { merge: true });

    if (typeof window !== 'undefined') {
      localStorage.setItem('verveai_launchpad_lead_id', leadId);
      localStorage.setItem('verveai_launchpad_lead_data', JSON.stringify({
        leadId,
        startupName,
        whatsapp: cleanedPhone,
        email: cleanedEmail,
        founderName,
      }));
    }

    return { success: true, leadId };
  } catch (err) {
    console.error('Error saving Step 1 lead to Firestore directly:', err);
    if (typeof window !== 'undefined') {
      localStorage.setItem('verveai_launchpad_lead_id', leadId);
    }
    return { success: true, leadId, offlineFallback: true };
  }
}

/**
 * STEP 2: The Sunk-Cost & Ego Investment
 * Stage: STEP_2_SCOPED | Score: 85/100 | Priority: URGENT PRIORITY
 */
export async function updateStep2Lead({
  leadId,
  domainPreference,
  existingDomain = '',
  industryVibe = '',
  aiGoal = '',
}) {
  if (!leadId) return { success: false, error: 'Missing leadId' };

  const capabilities = [
    'Website',
    'AI Agent',
    'Custom Domain Email',
    'Domain',
    industryVibe || 'Tech',
    aiGoal || 'Lead Capture',
  ];

  const payload = {
    inquiryId: leadId,
    leadId,
    stage: 'STEP_2_SCOPED',
    priority: 'urgent',
    score: 85,
    leadScore: 85,
    domainPreference,
    existingDomain: existingDomain ? existingDomain.trim() : null,
    industryVibe,
    aiGoal,
    serviceTags: capabilities,
    capabilities,
    message: `Scoped Blueprint: ${industryVibe || 'General'} | Goal: ${aiGoal || 'Lead Capture'} | Domain: ${domainPreference || 'New'} (${existingDomain || 'None'})`,
    updatedAt: serverTimestamp(),
    updatedAtIso: new Date().toISOString(),
  };

  // Dispatch to central API
  dispatchLeadToCentralApi(payload).catch(() => {});

  try {
    await updateDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload);
    try {
      await updateDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), payload);
    } catch {}
    return { success: true };
  } catch (err) {
    console.warn('Could not update Step 2 in Firestore (will merge on next step):', err);
    return { success: true, offlineFallback: true };
  }
}

/**
 * STEP 3: Payment Initiated
 * Stage: STEP_3_PAYMENT_INITIATED | Score: 95/100 | Priority: URGENT PRIORITY
 */
export async function markStep3PaymentInitiated(leadId) {
  if (!leadId) return;
  const payload = {
    inquiryId: leadId,
    leadId,
    stage: 'STEP_3_PAYMENT_INITIATED',
    priority: 'urgent',
    score: 95,
    leadScore: 95,
    paymentInitiatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  dispatchLeadToCentralApi(payload).catch(() => {});

  try {
    await updateDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload);
    try {
      await updateDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), payload);
    } catch {}
  } catch (err) {
    console.warn('Error marking payment initiated:', err);
  }
}

/**
 * STEP 3 Escape Hatch: "Have a question first? Talk to Launch Lead"
 * Stage: TALK_TO_LEAD_REQUESTED | Score: 95/100 | Priority: URGENT PRIORITY
 */
export async function markStep3TalkToLead(leadId, note = '') {
  if (!leadId) return;
  const consultationNote = note || 'Requested conversation with Launch Lead before payment';
  const payload = {
    inquiryId: leadId,
    leadId,
    stage: 'TALK_TO_LEAD_REQUESTED',
    priority: 'urgent',
    score: 95,
    leadScore: 95,
    requestedCallback: true,
    callbackRequestedAt: serverTimestamp(),
    consultationNote,
    message: `🚨 CONSULTATION REQUESTED: ${consultationNote}`,
    updatedAt: serverTimestamp(),
  };

  dispatchLeadToCentralApi(payload).catch(() => {});

  try {
    await updateDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload);
    try {
      await updateDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), payload);
    } catch {}
  } catch (err) {
    console.warn('Error marking talk to lead:', err);
  }
}

/**
 * STEP 3 CONVERSION: Successful Payment (₹4,999)
 * Stage: CONVERTED | Score: 100/100 | Creates record in clients collection
 */
export async function markStep3Converted({
  leadId,
  startupName,
  whatsapp,
  email,
  founderName,
  domainPreference,
  existingDomain,
  industryVibe,
  aiGoal,
  paymentId,
}) {
  const convertedPayload = {
    inquiryId: leadId,
    leadId,
    status: 'converted',
    stage: 'CONVERTED',
    priority: 'urgent',
    score: 100,
    leadScore: 100,
    paid: true,
    paidAmount: 4999,
    currency: 'INR',
    paymentId: paymentId || `pay_${Date.now()}`,
    message: `🎉 CONVERTED CLIENT: Payment verified ₹4,999 for ${startupName}`,
    paidAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  dispatchLeadToCentralApi(convertedPayload).catch(() => {});

  try {
    if (leadId) {
      await updateDoc(doc(db, INQUIRIES_COLLECTION, leadId), convertedPayload);
      try {
        await updateDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), convertedPayload);
      } catch {}
    }

    // Auto-create client project in `clients` collection
    const clientPayload = {
      name: founderName || startupName,
      companyName: startupName,
      startupName,
      email: (email || '').trim().toLowerCase(),
      phone: (whatsapp || '').trim(),
      whatsapp: (whatsapp || '').trim(),
      package: 'Starter Launchpad',
      amountPaid: 4999,
      currency: 'INR',
      status: 'In Progress',
      onboardingStatus: 'In Progress',
      projectStatus: 'In Progress',
      leadId: leadId || null,
      domainPreference: domainPreference || null,
      existingDomain: existingDomain || null,
      industryVibe: industryVibe || null,
      aiGoal: aiGoal || null,
      deliveryDeadline: '48-72 Hours',
      source: 'https://verveai.co',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const clientRef = await addDoc(collection(db, CLIENTS_COLLECTION), clientPayload);
    return { success: true, clientId: clientRef.id };
  } catch (err) {
    console.error('Error recording conversion in Firestore:', err);
    return { success: true, offlineFallback: true };
  }
}

/**
 * EXIT-INTENT MICRO-MODAL: "Have our Launch Architect call me in 15 mins"
 * Flags lead as URGENT PRIORITY • Score: 95/100 • Requested Callback
 */
export async function requestUrgentCallback({ leadId, startupName, whatsapp }) {
  const payload = {
    inquiryId: leadId,
    leadId,
    stage: 'CALLBACK_REQUESTED',
    priority: 'urgent',
    score: 95,
    leadScore: 95,
    requestedCallback: true,
    callbackRequestedAt: serverTimestamp(),
    callbackReason: 'Exit-intent micro-modal clicked',
    message: `🚨 EXIT-INTENT CALLBACK: Visitor requested 15-min call for ${startupName} (${whatsapp})`,
    updatedAt: serverTimestamp(),
  };

  dispatchLeadToCentralApi(payload).catch(() => {});

  try {
    if (leadId) {
      await updateDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload);
      try {
        await updateDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), payload);
      } catch {}
    }
    return { success: true };
  } catch (err) {
    console.warn('Could not update callback request:', err);
    return { success: true };
  }
}

/**
 * 1-CLICK INSTANT CALLBACK WITH MOBILE/OTP (OR EMAIL)
 * Stage: INSTANT_CALLBACK_REQUESTED | Score: 99/100 | Priority: URGENT PRIORITY
 * Dispatches IMMEDIATE ALERTS to Company Number (+91 9415967302) and contact@verveailabs.com
 */
export async function submitQuickCallbackLead({
  phone = '',
  email = '',
  contactMethod = 'phone',
  otpCode = '',
  name = '',
  startupName = '',
  note = '',
}) {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const cleanPhone = (phone || '').trim();
  const cleanEmail = (email || '').trim().toLowerCase();
  const digitsOnly = cleanPhone.replace(/[^0-9]/g, '').slice(-10);
  const leadId = `callback_${digitsOnly || 'user'}_${timestamp.toString(36)}_${randomSuffix}`;

  const displayName = name.trim() || (cleanPhone ? `Mobile Lead (${cleanPhone})` : `Email Lead (${cleanEmail})`);
  const effectiveStartup = startupName.trim() || (name.trim() ? `${name.trim()}'s Venture` : 'Instant Callback Lead');
  const alertBrief = `🚨 URGENT CALLBACK ALERT: User requested immediate 15-min call on ${cleanPhone || cleanEmail} for "${effectiveStartup}". Mobile verified with SMS OTP.`;

  const payload = {
    id: leadId,
    leadId,
    inquiryId: leadId,
    name: displayName,
    founderName: name.trim() || null,
    startupName: effectiveStartup,
    company: effectiveStartup,
    whatsapp: cleanPhone,
    phone: cleanPhone,
    phoneOrWhatsapp: cleanPhone,
    phoneVerified: true,
    email: cleanEmail,
    contactMethod,
    status: 'Urgent Callback Needed',
    stage: 'INSTANT_CALLBACK_REQUESTED',
    priority: 'urgent',
    score: 99,
    leadScore: 99,
    promoCode: 'LAUNCH50',
    discountClaimed: '50% OFF (₹4,999 Reserved)',
    packageType: 'Turnkey Startup Launchpad',
    finalPrice: 4999,
    currency: 'INR',
    requestedCallback: true,
    callbackRequestedAt: serverTimestamp(),
    otpVerified: true,
    otpCode: otpCode || 'SMS_VERIFIED',
    consultationNote: note || `Instant 15-minute callback requested for ${effectiveStartup}. Mobile verified with OTP.`,
    message: alertBrief,
    capabilities: ['Instant 15-Min Callback', 'Turnkey Startup Launchpad'],
    tags: ['Launchpad', 'Verified Mobile', 'Instant Callback', 'verveai.co'],
    source: 'https://verveai.co',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAtIso: new Date().toISOString(),
  };

  // 1. Dispatch immediately to central API -> fires email to contact@verveailabs.com AND phone alert to +91 9415967302
  dispatchLeadToCentralApi(payload).catch((apiErr) => {
    console.warn('[CALLBACK ALERT] Central API dispatch warning:', apiErr);
  });

  try {
    // 2. Persist directly to common `inquiries` collection in Firestore
    await setDoc(doc(db, INQUIRIES_COLLECTION, leadId), payload, { merge: true });

    // 3. Fallback write for legacy listeners
    try {
      await setDoc(doc(db, INQUIRIES_LEADS_COLLECTION, leadId), payload, { merge: true });
    } catch {}

    if (typeof window !== 'undefined') {
      localStorage.setItem('verveai_launchpad_lead_id', leadId);
      localStorage.setItem('verveai_launchpad_lead_data', JSON.stringify({
        leadId,
        whatsapp: cleanPhone,
        phone: cleanPhone,
        email: cleanEmail,
        founderName: name.trim(),
        startupName: payload.startupName,
        stage: 'INSTANT_CALLBACK_REQUESTED',
      }));
    }

    return { success: true, leadId };
  } catch (err) {
    console.error('Error saving quick callback lead directly to Firestore:', err);
    if (typeof window !== 'undefined') {
      localStorage.setItem('verveai_launchpad_lead_id', leadId);
    }
    return { success: true, leadId, offlineFallback: true };
  }
}

/**
 * ONE-CLICK RESUME: Fetch lead by ID
 */
export async function getLeadById(leadId) {
  if (!leadId) return null;

  try {
    // 1. Try common `inquiries` collection
    const inqSnap = await getDoc(doc(db, INQUIRIES_COLLECTION, leadId));
    if (inqSnap.exists()) {
      return { id: inqSnap.id, ...inqSnap.data() };
    }
  } catch (err) {
    console.error('Error fetching lead by ID:', err);
  }

  // 3. Fallback: check localStorage
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('verveai_launchpad_lead_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.leadId === leadId) {
          return parsed;
        }
      }
    } catch {}
  }

  return null;
}
